"use client";

import { LazyMotion, domAnimation, m, useReducedMotion } from "framer-motion";

import type { HomeDictionary } from "@/content/home";

interface HSEProps {
  copy: HomeDictionary["hse"];
}

export default function HSE({ copy }: HSEProps) {
  const shouldReduceMotion = useReducedMotion();
  const viewport = { once: true, amount: 0.2 };

  return (
    <LazyMotion features={domAnimation}>
      <section id="hse" className="relative py-32 overflow-hidden bg-[#0A111F]">
        <div className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat opacity-20 grayscale bg-[url('/images/texture.webp')]" />
        <div className="absolute inset-0 z-10 bg-[radial-gradient(circle_at_center,_transparent_0%,_#0A111F_85%)]" />

        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 relative z-20">
          <m.div
            initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: 34 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={viewport}
            transition={{ duration: 0.65, ease: "easeOut" }}
            className="flex flex-col mb-20"
          >
            <div className="flex items-center mb-6">
            <div className="w-8 h-[2px] bg-terracotta mr-4" />
            <span className="text-terracotta font-bold text-[10px] uppercase tracking-[0.3em] font-montserrat">
              {copy.eyebrow}
            </span>
            </div>

            <h2 className="font-montserrat font-black text-5xl md:text-7xl text-white uppercase tracking-tight leading-[0.9] mb-10">
            {copy.title} <br />
            <span className="text-terracotta">{copy.accent}</span>
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <p className="font-inter text-white font-bold text-xl md:text-2xl leading-snug">
              {copy.lead}
            </p>
            <p className="font-inter text-white/60 text-lg font-light leading-relaxed border-l border-white/10 pl-8">
              {copy.body}
            </p>
            </div>
          </m.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-16">
            {copy.cards.map((item, index) => (
              <m.div
              key={`${item.title}-${index}`}
              initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: 26 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={viewport}
              transition={{ duration: 0.55, delay: shouldReduceMotion ? 0 : index * 0.08, ease: "easeOut" }}
              className="bg-white/[0.03] backdrop-blur-md border border-white/10 p-10 flex flex-col hover:border-terracotta/40 hover:bg-white/[0.06] transition-all duration-500 group"
            >
              <div className="w-10 h-[1px] bg-terracotta/50 mb-8 group-hover:w-full transition-all duration-700" />

              <h4 className="text-white font-montserrat font-bold text-xl mb-3 leading-tight group-hover:text-terracotta transition-colors">
                {item.title}
              </h4>

              <p className="text-terracotta font-montserrat font-bold text-[10px] uppercase tracking-widest mb-6">
                {item.subtitle}
              </p>

              <p className="text-white/50 font-inter text-sm leading-relaxed font-light">
                {item.description}
              </p>

              <div className="mt-auto pt-8 flex justify-end">
                <span className="text-white/5 font-montserrat font-black text-4xl group-hover:text-terracotta/10 transition-colors">
                  0{index + 1}
                </span>
              </div>
              </m.div>
            ))}
          </div>
        </div>
      </section>
    </LazyMotion>
  );
}
